import React, { useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { validatePincodeByCountry } from './helper';
import pincodeService from './PincodeService';
import "./styles.css";
import { INPUT_AUTOFILL_PROPS } from '../../../../components/InputField/autofillProps';
import PreviousValue from '../../../../components/InputField/PreviousValue';
import { buildAutomationChildTestId, resolveAutomationTestId } from '../../../../components/InputField/testId';

function PincodeField(props) {
    const {
        id,
        countryCode,
        value: initialValue,
        onChange,
        label = "Pincode",
        isDisabled,
        required,
        className,
        showErr,
        prevValue,
        onError = () => { },
        onCommit,
        ...rest
    } = props;

    const [value, setValue] = useState("");
    const [error, setError] = useState(null);
    const [hasError, setHasError] = useState(false);
    const inputRef = useRef(null);
    const selectionRef = useRef({ start: 0, end: 0 });
    const fieldTestId = resolveAutomationTestId(props?.["data-testid"], id, label);

    // Update the field value if the initial value changes.
    useEffect(() => {
        const newValue = initialValue !== undefined && initialValue !== null
            ? String(initialValue).toUpperCase()
            : "";
        setValue(newValue);
    }, [initialValue]);

    // Cursor position preservation
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.setSelectionRange(
                selectionRef.current.start,
                selectionRef.current.end
            );
        }
    }, [value]);

    // Trigger fetching of pincode data (if not already loaded).
    useEffect(() => {
        if (Object.keys(pincodeService.records).length === 0) {
            pincodeService.fetch().catch(console.error);
        }
    }, []);

    // Handle change in the input.
    const handleChange = (newValue) => {
        setValue(newValue);
        if (onChange) {
            onChange(newValue);
        }
        // Reset any previous error.
        setError(null);
        onError(null);
        setHasError(false);
    };

    // Validate the pincode.
    // If shouldNotEmpty is true, then an empty value will produce an error.
    const validatePincode = (shouldNotEmpty) => {
        if (shouldNotEmpty) {
            if (!value) {
                setError("Please enter a valid zip code");
                onError("Please enter a valid zip code");
                setHasError(true);
                return;
            }
        } else {
            if (!value) return;
        }
        const isValid = validatePincodeByCountry(countryCode, value);
        if (!isValid) {
            setError("Please enter a valid zip code");
            setHasError(true);
            onError("Please enter a valid zip code");
        } else {
            setError(null);
            setHasError(false);
            onError(null);
        }
    };

    useEffect(() => {
        validatePincode();
    }, [countryCode, value]);

    useEffect(() => {
        if (showErr) {
            validatePincode(true);
        }
    }, [showErr]);

    return (
        <div className={`${className ? className : ''}`} data-testid={fieldTestId}>
            <div className="inputfield__container">
                <div className="inputfield__innercontainer_outline">
                    <div className="inputfield ps-0 " >
                        <input
                            ref={inputRef}
                            id={id}
                            data-testid={buildAutomationChildTestId(fieldTestId, "input")}
                            {...INPUT_AUTOFILL_PROPS}
                            className={`inputfield__input ${hasError ? 'error' : ''} ${isDisabled ? "cursor-disabled" : ""}`}
                            value={value}
                            onChange={(e) => {
                                const newValue = e.target.value.toUpperCase();
                                const start = e.target.selectionStart;
                                const end = e.target.selectionEnd;
                                selectionRef.current = { start, end };
                                handleChange(newValue);
                            }}
                            onBlur={() => {
                                validatePincode();
                                onCommit?.(value);
                            }}
                            disabled={isDisabled}
                            {...rest}
                            style={{
                                height: "50px",
                                borderRadius: "15px",
                                paddingLeft: "15px",
                                border: "none",
                                fontSize: "14px",
                                outline: "none",
                                backgroundColor: prevValue?.has ? "#FFF9D6" : isDisabled ? "#F6F6F6" : "#F1FAFC",
                                width: "100%"
                            }}
                        />
                    </div>
                    <em className="inputfield__desc pt-0 d-block" data-testid={buildAutomationChildTestId(fieldTestId, "description")}>
                        {"E.g. 80203"}
                    </em>
                </div>
            </div>

            <PreviousValue
                show={prevValue?.has}
                value={prevValue?.value}
                testId={buildAutomationChildTestId(fieldTestId, "previous")}
            />

            {error && (hasError || showErr) && (
                <em className="inputfield__error" data-testid={buildAutomationChildTestId(fieldTestId, "error")}>
                    {error}
                </em>
            )}
        </div>
    );
}

export default observer(PincodeField);
